import { registerSW } from "virtual:pwa-register";

type RefreshCallback = () => void;

const LOGIN_AUTO_UPDATE_KEY = "tm_login_auto_update_in_progress";

let _updateSW: (reloadPage?: boolean) => void = () => {};
let _onNeedRefresh: RefreshCallback | null = null;
let _registration: ServiceWorkerRegistration | undefined;

let _isApplyingUpdate = false;

const getLoginAutoUpdateInProgress = () =>
    sessionStorage.getItem(LOGIN_AUTO_UPDATE_KEY) === "1";

const setLoginAutoUpdateInProgress = (value: boolean) => {
    if (value) {
        sessionStorage.setItem(LOGIN_AUTO_UPDATE_KEY, "1");
        return;
    }

    sessionStorage.removeItem(LOGIN_AUTO_UPDATE_KEY);
};

_updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
        _onNeedRefresh?.();
    },
    onOfflineReady() {
        console.log("✅ offline ready");
    },
    onRegistered(registration) {
        if (!registration) return;

        _registration = registration;

        // If no update is waiting after a fresh load, the previous login auto-update cycle finished.
        if (!registration.waiting) {
            setLoginAutoUpdateInProgress(false);
        }
    },
});

export const applyUpdate = () => {
    if (_isApplyingUpdate) return;
    _isApplyingUpdate = true;

    try {
        const registration = _registration;

        if (registration?.waiting) {
            navigator.serviceWorker.addEventListener(
                "controllerchange",
                () => window.location.reload(),
                { once: true },
            );
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
            return;
        }

        _updateSW(true);
    } catch {
        _isApplyingUpdate = false;
    }
};

export const checkUpdate = async (): Promise<boolean> => {
    if (!_registration) {
        throw new Error("Service worker is not registered yet");
    }

    if (_registration.waiting) {
        return true;
    }

    return new Promise<boolean>((resolve) => {
        const timeout = setTimeout(() => {
            cleanup();
            resolve(false);
        }, 5000);

        const onUpdateFound = () => {
            cleanup();
            resolve(true);
        };

        const cleanup = () => {
            clearTimeout(timeout);
            _registration!.removeEventListener("updatefound", onUpdateFound);
        };

        _registration!.addEventListener("updatefound", onUpdateFound);
        _registration!.update().catch(() => {
            cleanup();
            resolve(false);
        });
    });
};

export const setOnNeedRefresh = (cb: RefreshCallback) => {
    _onNeedRefresh = cb;

    return () => {
        if (_onNeedRefresh === cb) {
            _onNeedRefresh = null;
        }
    };
};

export const canApplyLoginAutoUpdate = () => !getLoginAutoUpdateInProgress();

export const markLoginAutoUpdateStarted = () => {
    setLoginAutoUpdateInProgress(true);
};
